import {
  useFolderStructure,
  useStoryManagerView,
} from '@src/stores-v2/StoreContext';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import HomeIcon from '@src/svgs/HomeIcon';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import styled from 'styled-components';

import { dropTargetHighlight } from './dragStyles';
import FolderIcon from './FolderIcon';
import { useStoryDropTarget } from './useStoryDropTarget';

import type { Folder } from '@src/types.ts/story';
import type { FC } from 'react';

type StoriesDroppedHandler = (
  storyIds: string[],
  folderId: string | null,
) => void;

// ─── Recursive tree node ────────────────────────────────────────────────────

type NodeProps = {
  folder: Folder;
  depth: number;
  currentFolderId: string | null;
  expandedIds: Set<string>;
  foldersByParent: Map<string | null, Folder[]>;
  onSelect: (id: string) => void;
  onToggleExpand: (id: string) => void;
  onStoriesDropped?: StoriesDroppedHandler;
};

const FolderTreeNode: FC<NodeProps> = ({
  folder,
  depth,
  currentFolderId,
  expandedIds,
  foldersByParent,
  onSelect,
  onToggleExpand,
  onStoriesDropped,
}) => {
  const children = foldersByParent.get(folder.id) ?? [];
  const hasChildren = children.length > 0;
  const isExpanded = expandedIds.has(folder.id);
  const isSelected = folder.id === currentFolderId;
  const { isDropTarget, dropHandlers } = useStoryDropTarget(
    folder.id,
    onStoriesDropped,
  );

  return (
    <>
      <NodeRow
        $depth={depth}
        $selected={isSelected}
        onClick={() => onSelect(folder.id)}
        {...dropHandlers}
        data-drop-target={isDropTarget || undefined}
        data-testid={`sidebar-folder-${folder.id}`}
      >
        <ExpandArrow
          $hasChildren={hasChildren}
          $expanded={isExpanded}
          onClick={e => {
            if (!hasChildren) return;
            e.stopPropagation();
            onToggleExpand(folder.id);
          }}
        >
          {hasChildren && <ChevronDownIcon width="12" height="12" />}
        </ExpandArrow>
        <FolderIcon
          width={16}
          height={16}
          style={{ flexShrink: 0, color: 'var(--text-muted)' }}
        />
        <NodeName>{folder.name}</NodeName>
      </NodeRow>
      {isExpanded &&
        children.map(child => (
          <FolderTreeNode
            key={child.id}
            folder={child}
            depth={depth + 1}
            currentFolderId={currentFolderId}
            expandedIds={expandedIds}
            foldersByParent={foldersByParent}
            onSelect={onSelect}
            onToggleExpand={onToggleExpand}
            onStoriesDropped={onStoriesDropped}
          />
        ))}
    </>
  );
};

// ─── Sidebar ────────────────────────────────────────────────────────────────

type SidebarProps = {
  /** Current panel width in px (driven by the resizable TreePanel). */
  width: number;
  onStoriesDropped?: StoriesDroppedHandler;
};

const FolderTreeSidebar: FC<SidebarProps> = observer(
  ({ width, onStoriesDropped }) => {
    const store = useFolderStructure();
    // Tree expansion lives in the view store so it survives navigation.
    const view = useStoryManagerView();
    const expandedIds = view.expandedFolderIds;
    const showcaseExpanded = view.showcaseTreeExpanded;

    // Showcase root accepts dropped stories → moves them to the root (unfolders).
    const showcaseDrop = useStoryDropTarget(null, onStoriesDropped);

    // Auto-expand ancestors of the current folder when navigation changes
    useEffect(() => {
      if (!store.currentFolderId) return;
      const ancestors = store.breadcrumbForFolder(store.currentFolderId);
      view.addExpandedFolders(ancestors.map(f => f.id));
    }, [store, view, store.currentFolderId]);

    const toggleExpand = (id: string) => view.toggleFolderExpanded(id);

    const rootFolders = store.foldersByParent.get(null) ?? [];

    return (
      <SidebarWrapper style={{ width }}>
        <TreeScroll>
          <NodeRow
            $depth={0}
            $selected={store.currentFolderId === null}
            onClick={() => store.goToRoot()}
            {...showcaseDrop.dropHandlers}
            data-drop-target={showcaseDrop.isDropTarget || undefined}
            data-testid="sidebar-showcase-root"
          >
            <ExpandArrow
              $hasChildren={rootFolders.length > 0}
              $expanded={showcaseExpanded}
              onClick={e => {
                if (rootFolders.length === 0) return;
                e.stopPropagation();
                view.setShowcaseTreeExpanded(!showcaseExpanded);
              }}
            >
              {rootFolders.length > 0 && (
                <ChevronDownIcon width="12" height="12" />
              )}
            </ExpandArrow>
            <ShowcaseIconWrap>
              <HomeIcon />
            </ShowcaseIconWrap>
            <NodeName>Workspace</NodeName>
          </NodeRow>
          {showcaseExpanded &&
            rootFolders.map(folder => (
              <FolderTreeNode
                key={folder.id}
                folder={folder}
                depth={1}
                currentFolderId={store.currentFolderId}
                expandedIds={expandedIds}
                foldersByParent={store.foldersByParent}
                onSelect={id => store.enterFolder(id)}
                onToggleExpand={toggleExpand}
                onStoriesDropped={onStoriesDropped}
              />
            ))}
        </TreeScroll>
      </SidebarWrapper>
    );
  },
);

export default FolderTreeSidebar;

// ─── Styles ─────────────────────────────────────────────────────────────────

const SidebarWrapper = styled.div`
  /* width is set inline by the parent (resizable TreePanel). */
  flex-shrink: 0;
  /* Float as a light panel sized to its content, not the full page height.
     Top-aligns with the folder grid on the right (both start at the
     OuterWrapper's 40px-below-header content edge). */
  align-self: flex-start;
  max-height: 100%;
  background: var(--sidebar);
  border-radius: var(--radius-card);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const TreeScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 10px 0 8px;
`;

const INDENT_PX = 16;

const NodeRow = styled.div<{ $depth: number; $selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  /* side margin + border-radius creates the floating pill look */
  margin: 1px 6px;
  padding: 5px 10px 5px ${({ $depth }) => `${8 + $depth * INDENT_PX}px`};
  cursor: pointer;
  border-radius: 8px;
  background: ${({ $selected }) =>
    $selected
      ? 'color-mix(in srgb, var(--tree-selection) 18%, transparent)'
      : 'transparent'};
  color: ${({ $selected }) =>
    $selected ? 'var(--text-primary)' : 'var(--text-secondary)'};
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-regular);
  transition:
    background 100ms ease,
    box-shadow 100ms ease;

  &:hover {
    background: ${({ $selected }) =>
      $selected
        ? 'color-mix(in srgb, var(--tree-selection) 22%, transparent)'
        : 'var(--surface)'};
    color: var(--text-primary);
  }

  &[data-drop-target] {
    ${dropTargetHighlight}
    color: var(--text-primary);
  }
`;

const ExpandArrow = styled.span<{
  $hasChildren: boolean;
  $expanded: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  flex-shrink: 0;
  color: var(--text-muted);
  cursor: ${({ $hasChildren }) => ($hasChildren ? 'pointer' : 'default')};
  opacity: ${({ $hasChildren }) => ($hasChildren ? 1 : 0)};

  /* Chevron points down when expanded, right when collapsed. */
  & > svg {
    transition: transform 150ms ease;
    transform: rotate(${({ $expanded }) => ($expanded ? '0deg' : '-90deg')});
  }
`;

// Wraps HomeIcon (the showcase root marker), normalizing it to 16px and
// matching the muted color of the folder icons.
const ShowcaseIconWrap = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--text-muted);

  svg {
    width: 16px;
    height: 16px;
  }
`;

const NodeName = styled.span`
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
